import { ExternalLink, Landmark, Map, ScrollText, Receipt, Gavel, ShieldAlert } from "lucide-react";
import { fmtDate } from "@/lib/constants";

function LinkButton({ url, icon: Icon, children, testid }) {
  const disabled = !url;
  return (
    <a href={url || undefined} target={disabled ? undefined : "_blank"} rel="noopener noreferrer"
      data-testid={testid} aria-disabled={disabled}
      className={`flex items-center justify-between gap-3 px-4 py-3 rounded-md border text-sm font-medium transition-colors duration-150 ${disabled ? "border-border bg-secondary/40 text-muted-foreground cursor-not-allowed pointer-events-none" : "border-border bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary"}`}
      onClick={(e) => { if (disabled) e.preventDefault(); }}>
      <span className="flex items-center gap-2"><Icon className="w-4 h-4" /> {children}</span>
      {!disabled && <ExternalLink className="w-3.5 h-3.5 opacity-60" />}
    </a>
  );
}

export default function CountyRecordsLinks({ office, hasCounty = true, verifiedBy = "Acquisition Manager" }) {
  if (!office) {
    return (
      <div className="bg-card border border-border rounded-md" data-testid="county-records-empty">
        <div className="px-5 py-3 border-b border-border font-display font-bold flex items-center gap-2"><Landmark className="w-4 h-4 text-accent" /> County Records</div>
        <div className="p-5 text-sm text-muted-foreground">
          {hasCounty
            ? "No official county office is configured for this county yet. Add it in Settings → County Offices to enable one-click record verification."
            : "This property record is missing its state and county. Add them to the property so the matching county office can be linked."}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md" data-testid="county-records-panel">
      <div className="px-5 py-3 border-b border-border font-display font-bold flex items-center gap-2"><Landmark className="w-4 h-4 text-accent" /> Official County Records</div>
      <div className="p-5">
        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4">
          {office.county_name} County, {office.state_abbreviation || office.state_name}{office.office_name ? ` · ${office.office_name}` : ""}
        </div>
        <div className="grid gap-2">
          <LinkButton url={office.property_search_url} icon={Landmark} testid="county-assessor-link">Verify Assessor Record</LinkButton>
          <LinkButton url={office.gis_map_url} icon={Map} testid="county-gis-link">Open Parcel Map</LinkButton>
          <LinkButton url={office.recorder_url} icon={ScrollText} testid="county-recorder-link">Search Recorded Deeds</LinkButton>
          <LinkButton url={office.tax_collector_url} icon={Receipt} testid="county-tax-link">Check Property Taxes</LinkButton>
          <LinkButton url={office.foreclosure_search_url} icon={Gavel} testid="county-foreclosure-link">Check Foreclosure Records</LinkButton>
        </div>

        <div className="mt-4 text-xs text-muted-foreground border-t border-border pt-3 space-y-0.5" data-testid="county-verified-meta">
          <div>Last independently verified: <span className="text-foreground font-medium">{fmtDate(office.last_verified_date)}</span></div>
          <div>Verified by: <span className="text-foreground font-medium">{office.verified_by || verifiedBy}</span></div>
          {office.phone_number && <div>County phone: <span className="text-foreground font-medium">{office.phone_number}</span></div>}
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2" data-testid="county-verify-warning">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <span>County information must be independently verified before making an offer, calculating equity, or relying on a foreclosure deadline.</span>
        </div>
      </div>
    </div>
  );
}
